import { useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { showSessionExpiryToast } from "../utils/showSessionExpiryToast";
import { showError } from "../utils/error-handling";
import { useRefreshToken } from "../data/useRefreshToken";
import { env } from "../utils/env";
import { DateTime } from "luxon";
import { useCookies } from "react-cookie";
import { AUTH_COOKIE_NAME, useAuth } from "../context/AuthContext";

export const SessionManager = () => {
  const { credentials, logout, setCredentials } = useAuth();
  const [, setCookie] = useCookies();
  const expiresAtRef = useRef<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { mutate: refresh } = useRefreshToken();

  const handleLogout = useCallback(() => {
    toast.dismiss("session-expiry");
    logout();
  }, [logout]);

  const handleStayLoggedIn = useCallback(() => {
    toast.dismiss("session-expiry");

    refresh(
      { refresh: credentials?.refreshToken },
      {
        onSuccess: (res) => {
          const newCreds = {
            ...credentials,
            accessToken: res.access,
            refreshToken: credentials?.refreshToken,
            expires_in: res.expires_in,
            expiresAt: Date.now() + res.expires_in * 1000,
          };
          setCredentials(newCreds);

          setCookie(AUTH_COOKIE_NAME, newCreds, {
            expires: DateTime.now()
              .plus({ milliseconds: env.cookieExpiryMs })
              .toJSDate(),
            path: "/",
            secure: true,
            sameSite: "strict",
          });
        },
        onError: (err: any) => {
          showError(err, "Problem with refresh token, please login again.");
          logout();
        },
      }
    );
  }, [credentials, logout, refresh, setCookie, setCredentials]);

  useEffect(() => {
    if (!credentials?.expires_in) return;

    if (intervalRef.current) clearInterval(intervalRef.current);

    const expiresAt = credentials.expiresAt;
    expiresAtRef.current = expiresAt;

    intervalRef.current = setInterval(() => {
      const now = Date.now();
      const timeLeft = expiresAt - now;
      if (timeLeft <= 60000 && !toast.isActive("session-expiry")) {
        showSessionExpiryToast(
          Math.floor(timeLeft / 1000),
          handleStayLoggedIn,
          handleLogout
        );
      }

      if (timeLeft <= 0) {
        clearInterval(intervalRef.current!);
        toast.dismiss("session-expiry");
        logout();
      }
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [credentials, handleStayLoggedIn, handleLogout, logout]);

  return null;
};
